import Clickable from '@carvana/merch-components/Clickable';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Text } from '@/vdp/styles';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useModalContext } from '~/context/vdp/ModalContext/ModalContext';
import type { WhyBuyNarrativeItem } from '~/hooks/vdp/selectors/useWhyBuyNarratives';
import getWhyBuyMediaProps from '~/utilities/vdp/getWhyBuyMediaProps';
import useWhyBuyCardVideo from './hooks/useWhyBuyCardVideo';
import useWhyBuyStillImage from './hooks/useWhyBuyStillImage';
import StockImageBadge from './StockImageBadge';
import WhyBuyMakePlaceholder from './WhyBuyMakePlaceholder';
import {
  Details,
  ImageryCard,
  ImageryCardContent,
  ImageryWrapper,
  StyledImage,
  StyledVideo
} from './WhyBuyWithImageryCard.styles';

type OwnProps = {
  bulletPoint: WhyBuyNarrativeItem;
  isActive: boolean;
  onActivate: () => void;
};

const IMAGE_HEIGHT = 170;

export default function WhyBuyWithImageryCard({
  bulletPoint,
  isActive,
  onActivate
}: OwnProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { setWhyBuyModalOpen } = useModalContext();
  const trackEvent = useTrackEvent();
  const [failedVideoUrl, setFailedVideoUrl] = useState<string>();
  const { imageUrl, isStockImage, videoUrl } = useMemo(
    () => getWhyBuyMediaProps(bulletPoint.media, { height: IMAGE_HEIGHT }),
    [bulletPoint.media]
  );
  const { isFailed: isImageFailed, url: loadedImageUrl } = useWhyBuyStillImage(
    imageUrl,
    'WhyBuyWithImageryCard'
  );
  const hasMedia = Boolean(imageUrl || videoUrl);
  const isVideoFailed = Boolean(videoUrl) && failedVideoUrl === videoUrl;
  const isVideoShown = Boolean(videoUrl) && !isVideoFailed;
  const isMediaShown = isVideoShown || Boolean(loadedImageUrl);
  const isPlaceholderShown =
    !isMediaShown && (!hasMedia || isVideoFailed || isImageFailed);
  const handleMediaError = useCallback(
    () => setFailedVideoUrl(videoUrl),
    [videoUrl]
  );
  const { onError, onMouseEnter, onMouseLeave, videoRef } = useWhyBuyCardVideo({
    cardRef,
    isActive,
    isMobile,
    onActivate,
    onMediaError: handleMediaError,
    src: videoUrl
  });

  const handleClick = () => {
    setWhyBuyModalOpen(true, { openTo: bulletPoint.id });
    trackEvent('Why Buy Feature', {
      description: 'why buy feature',
      item: bulletPoint.title
    });
  };

  return (
    <Clickable
      data-adobe-id={null}
      data-analytics-id={null}
      data-testid="why-buy-imagery-card"
      element={ImageryCard}
      elementRef={cardRef}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <ImageryWrapper>
        {isVideoShown && (
          <StyledVideo
            data-testid="why-buy-imagery-video"
            draggable={false}
            muted
            onError={onError}
            playsInline
            preload="metadata"
            ref={videoRef}
            src={videoUrl}
          />
        )}
        {loadedImageUrl && (
          <StyledImage
            alt={bulletPoint.title}
            data-testid="why-buy-imagery-image"
            draggable={false}
            loading="lazy"
            src={loadedImageUrl}
          />
        )}
        {isPlaceholderShown && (
          <WhyBuyMakePlaceholder bulletType={bulletPoint.bulletType} />
        )}
        {isMediaShown && isStockImage && <StockImageBadge />}
      </ImageryWrapper>
      <ImageryCardContent>
        <div className="line-clamp-4">
          <h3 className="mb-lg text-text-strong t-heading-sm">
            {bulletPoint.title}
          </h3>
          <Text>{bulletPoint.content}</Text>
        </div>
        <Details data-testid="why-buy-details">Details</Details>
      </ImageryCardContent>
    </Clickable>
  );
}
